<template>
  <main
    class="wrapper-content px-0 bg-light"
    :class="{ 'd-flex flex-column justify-content-between pt-4 bg-white': isError }"
  >
    <!-- Loading -->
    <LmComponentLoading v-if="isLoading" fixed />

    <section class="page-content bg-white" v-if="!isError">
      <div class="px-3">
        <portal to="documentSubTitle">
          <div class="col-12 px-0">
            <h5>Tipo de documento: {{ documentTypeName }}</h5>
          </div>
        </portal>
        <portal to="documentName">
          <!-- Input del nombre -->
          <LmTextInput
            class="col-12 px-0"
            id="documentName"
            v-model="$v.form.documentName.$model"
            :v="validation('form.documentName')"
            :label="$t('name')"
            :placeholder="$t('name')"
            :disabled="(!canBeEditedByMe && isOnEditMode) || (isStarted && lumbreVersion === 2)"
          />
        </portal>
        <!-- Formbuilder Server Components -->
        <LmFormBuilder
          v-if="showFormBuilder"
          id="lmFormbuilder"
          class="px-0"
          ref="lmFormbuilder"
          :isDisabled="disableFormbuilder"
          :endpointUriwhereGetComponents="formbuilderPath"
          :endpointUriWhereGetValuesToLoadInEditMode="dataPathOnEditMode"
          :contentTemplateData="form"
          :editors="form.collaborators"
          @formObjectToSendToServerChange="updateForm($event)"
          @editor-change="onEditorChange"
          @hide-collaborators-workflow="onHideCollaborators"
        />

        <!-- Vista en modo PDF -->
        <div class="wrapper-content px-0">
          <div v-if="showDocumentViewer" class="row w-100 mx-0">
            <div class="col-sm-12 col-md-12 w-100 my-2">
              <div class="mt-0 d-flex justify-content-between">
                <h2 class="m-0 d-flex align-items-center">
                  {{ form.documentName }}
                  <span v-if="pdfSize" style="font-size: .9rem" class="ml-3">
                    <b-badge size="sm" :variant="sizeIndicator(pdfSize)">{{ formatBytes(pdfSize) }}</b-badge>
                  </span>
                </h2>
                <div v-if="hasAssociatedProcesses" class="d-flex align-items-center">
                  <template v-if="associatedProcesses.length === 1">
                    <div>
                      <div class="text-uppercase" style="font-size: .65rem; text-align: initial">{{ $t('related_process') }}</div>
                      <b-link :to="`/tramites/${associatedProcesses[0].processTypeId}/editar/${associatedProcesses[0]._id}?viewer`">
                        <h4 class="mt-1">{{ associatedProcesses[0].processNumber }}</h4>
                      </b-link>
                    </div>
                  </template>
                  <template v-else>
                    <span class="mt-1 text-primary font-weight-bold clickable" @click="showAssociatedProcesses = true">{{ associatedProcesses.length }} trámites asociados</span>
                    <a-modal v-model="showAssociatedProcesses" title="Trámites asociados" :footer="null">
                      <div v-for="(item, index) of associatedProcesses" :key="index">
                        <b-link :to="`/tramites/${item.processTypeId}/editar/${item._id}?viewer`">
                          <span class="text-uppercase" style="font-size: .65rem">{{ item.processName }}</span>
                          <h4 class="mt-1">{{ item.processNumber }}</h4>
                        </b-link>
                      </div>
                    </a-modal>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showDocumentViewer" class="row w-100 mx-0">
            <div :class="stepsColumns" class="w-100">
              <a-steps :current="form.documentSteps.position" size="small" :direction="stepsDirection" v-if="lumbreVersion === 1">
                <a-step
                  class="mt-2 mb-2"
                  v-for="(item, index) in form.documentSteps.items"
                  :key="`step-${index}`"
                  :title="item.title" :description="item.description"
                />
                </a-steps>
            </div>
            <div :class="pdfViewerColumns" class="position-relative w-100" v-if="documentsPdfList.length">
              <b-button
                v-if="documentsPdfList.length > 1"
                class="position-absolute prev-btn"
                variant="primary"
                @click="documentsPdfIndex--"
                :disabled="documentsPdfIndex === 0"
              >
                <i class="mdi mdi-chevron-left"></i>
              </b-button>
              <iframe
                id="pdfViewer"
                :src="documentsPdfList[documentsPdfIndex].path"
                :height="pdfViewerHeight"
                :key="documentInPdfKey"
                width="100%"
              />
              <b-button
                v-if="documentsPdfList.length > 1"
                class="position-absolute next-btn"
                variant="primary"
                @click="documentsPdfIndex++"
                :disabled="documentsPdfIndex === documentsPdfList.length - 1"
              >
                <i class="mdi mdi-chevron-right"></i>
              </b-button>
            </div>
            <div v-else class="col-12">
              <LmSvgIcon
                icon="LmNoDataIcon"
                wrapperClass="my-4 d-flex align-items-center flex-column"
                text="El documento no pudo generarse, por favor tómelo o rechácelo y luego intente reducir su tamaño"
                width="12em"
              />
            </div>
          </div>

          <LmDocumentWorkflow
            class="my-4"
            ref="documentWorkflow"
            v-if="lumbreVersion === 2 && isOnEditMode && showDocumentInPdf"
            :isOnEditMode="isOnEditMode"
            v-model="form.workflow"
          />

          <!-- Doc Settings -->
          <div v-if="canBeEditedByMe || !isOnEditMode">
            <h3>Configuraciones del documento</h3>

            <b-card no-body class="border my-4">
              <b-tabs card>
                <b-tab title="Participantes" key="signers" v-if="canBeEditedByMe || !isOnEditMode">

                  <template v-if="lumbreVersion == 1 && documentHasWorkflowSections">
                    <div v-for="(workflowSection, index) in workflowSections" :key="workflowSection.id" >
                      <hr v-if="index > 0" class="dotted-hr mt-4 mb-4">
                      <h3>{{index + 1}} - {{workflowSectionTitle(workflowSection)}}</h3>
                      <LmParticipants
                        :disabled="workflowSectionIsFixed(workflowSection)"
                        :showWorkflowChangeSection="workflowSectionIsDynamic(workflowSection)"
                        :label="$t('users')"
                        :title="workflowSectionIsDynamic(workflowSection) ? $t('lm_participants_title') : $t('lm_fixed_participants_title')"
                        :subtitle="workflowSectionIsDynamic(workflowSection) ? $t('lm_participants_subtitle') : ''"
                        v-model="form.workflowSectionsParticipants[index].participants"
                        :canAddReviewers="canAddReviewers"
                        :canAddExternalParticipants="canAddExternalParticipants"
                        :allowInPersonParticipant="allowInPersonParticipant"
                        :showReviewersGroups="form.workflowSectionsParticipants[index].showReviewersGroups"
                        :participantIndex="index"
                      />
                    </div>
                  </template>

                  <LmParticipants
                    v-if="lumbreVersion == 1 && !documentHasWorkflowSections"
                    :label="$t('users')"
                    placeholder="Seleccione los usuarios que van a participar de este documento"
                    v-model="form.participants"
                    :canAddReviewers="canAddReviewers"
                    :canAddExternalParticipants="canAddExternalParticipants"
                    :allowInPersonParticipant="allowInPersonParticipant"
                  />
                  <LmDocumentWorkflow
                    ref="documentWorkflow"
                    v-if="lumbreVersion == 2 && !isLoading"
                    :isOnEditMode="isOnEditMode"
                    v-model="form.workflow"
                  />
                </b-tab>
                <b-tab title="Destinatarios" key="recipients" v-if="(canBeEditedByMe || !isOnEditMode) && isComunicable && lumbreVersion === 1">
                  <LmRecipients
                    v-model="form.recipients"
                    :canAddExternalRecipients="canAddExternalRecipients"
                  />
                </b-tab>
                <b-tab title="Compartir con" key="sharewith" v-if="canBeEditedByMe || !isOnEditMode">
                  <LmShareWith
                    v-model="form.shareWith"
                  />
                </b-tab>
                <b-tab title="Visualizadores" key="viewers" v-if="!isPublic && (canBeEditedByMe || !isOnEditMode)">
                  <LmViewers
                    v-model="form.viewers"
                  />
                </b-tab>
              </b-tabs>
            </b-card>
          </div>
        </div>
      </div>
      <!-- Toolbar -->
      <LmToolbar
        ref="toolbar"
        :toolbarHeight="toolbarHeight"
        :isOnEditMode="isOnEditMode"
        :items="toolbarItems"
        :config="toolbarConfig"
        @onContentTemplateChange="onContentTemplateChange"
      />
    </section>

    <LmSvgIcon
      v-if="isError"
      icon="LmAccessDeniedIcon"
      wrapperClass="my-4 d-flex align-items-center flex-column"
      text="No pudimos cargar el documento solicitado."
      width="12em"
    />

    <!-- Action Buttons -->
    <section class="mt-4 px-3 d-flex gap-3">
      <LmButton
        :value="fromProcess ? $t('go_back_to_process') : $t('cancel')"
        type="button"
        buttonClass="btn-secondary"
        @click="onCancel()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="showSubmitButton"
        :value="isStarted ? $t('save') : $t('save_as_draft')"
        type="button"
        :buttonClass="isStarted ? 'btn-success' : 'btn-primary'"
        @click="onSubmit()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="!isOnEditMode"
        :value="$t('start_document')"
        type="button"
        buttonClass="btn-success"
        @click="onSendToStart('create')"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && (lumbreVersion === 1 && canBeEditedByMe) || (lumbreVersion === 2 && actionsAvailables.canBeStartedByMe)"
        :value="$t('start_document')"
        type="button"
        buttonClass="btn-success"
        @click="onSendToStart('update')"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && canBeSignedByMe"
        :value="$t('sign')"
        type="button"
        buttonClass="btn-success"
        @click="onBeforeSign()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && canBeReviewedByMe"
        :value="$t('approve')"
        type="button"
        buttonClass="btn-success"
        @click="onReviewConfirmation()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && canBeRejectedByMe || (lumbreVersion === 2 && actionsAvailables.canBeRejectedByMe)"
        :value="$t('reject')"
        type="button"
        buttonClass="btn-danger"
        @click="onReject()"
        requireConfirmation
        confirmationTitle="¿Estas seguro de rechazar el documento?"
        confirmationMessage="El documento se pondrá en edición y se notificará al creador."
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && (canBeEditedByMe && lumbreVersion === 1) || (lumbreVersion === 2 && actionsAvailables.canBeDeletedByMe)"
        :value="$t('delete')"
        type="button"
        action="delete"
        buttonClass="btn-danger"
        @click="onDelete()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="isOnEditMode && (lumbreVersion === 2 && actionsAvailables.canBeTakedByMe)"
        value="Tomar"
        type="button"
        buttonClass="btn-primary"
        @click="onTake()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="canReSendEmailToExternalSigner"
        :value="`Reenviar Email a ${lumbreNextParticipant?.firstname + ' ' + lumbreNextParticipant?.lastname}`"
        type="button"
        buttonClass="btn-info"
        @click="onResendTokenToSigner()"
        :is-processing="isProcessing"
      />
      <LmButton
        v-if="canDelegateSignature"
        :value="$t('inperson_sign', {
          name: this.nextParticipantName
        })"
        type="button"
        buttonClass="btn-info"
        @click="onBeforeSign()"
        :is-processing="isProcessing"
      />
    </section>
    <lmConfirmationModal
      ref="lmConfirmationModalFirmarToken" title="Aviso"
      message="Va a iniciar el proceso de firma del documento.  ¿Está seguro?"
    />
    <lmConfirmationModal ref="lmConfirmationModalLicenses" title="Aviso" />
    <LmSendOTP
      ref="lmValidationModalFirmarToken" title="Aviso"
      message="Un código fue enviado a su mail, por favor ingreselo a continuación." @ok="setToken"
    />
    <LmSignaturePad ref="lmSignaturePad" @cancel="isProcessing = false"/>
    <WacomSignaturePad ref="WacomSignaturePad" @cancel="isProcessing = false"/>
  </main>
</template>

<script>
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmFormBuilder from '@/components/LmFormBuilder'
import LmParticipants from '@/components/form/elements/LmParticipants'
import LmDocumentWorkflow from '@/components/LmDocumentWorkflow'
import LmRecipients from '@/components/form/elements/LmRecipients'
import LmShareWith from '@/components/form/elements/LmShareWith'
import LmViewers from '@/components/form/elements/LmViewers'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'
import lmConfirmationModal from '@/components/modals/LmConfirmationModal'
import LmSendOTP from '@/components/modals/LmSendOTP'
import lmFormatBytes from '@/mixins/lmFormatBytes.js'
import LmSignaturePad from '@/components/LmSignaturePad'
import WacomSignaturePad from '@/components/WacomSignaturePad'
import LmToolbar from '@/components/lmToolBar'
import lmWorkflowBuilderUi from '@/mixins/lmWorkflowBuilderUi.js'
import lmDocumentsMixin from '@/mixins/lmDocumentsMixin.js'

import _ from 'lodash'

import { mapMutations, mapGetters } from 'vuex'

import {
  required
} from 'vuelidate/lib/validators'

export default {
  mixins: [lmDocumentsMixin, lmToasts, lmFormatBytes, lmWorkflowBuilderUi],
  provide () {
    const document = {}
    for (const key in this.context) {
      Object.defineProperty(document, key, {
        enumerable: true,
        get: () => this.context[key]
      })
    }
    return { document, toolbarCtx: 'documents' }
  },
  components: {
    LmToolbar,
    LmFormBuilder,
    LmButton,
    LmParticipants,
    LmRecipients,
    LmShareWith,
    LmTreeSelect,
    LmTextInput,
    lmConfirmationModal,
    LmSendOTP,
    LmViewers,
    LmDocumentWorkflow,
    LmSignaturePad,
    WacomSignaturePad
  },
  props: {
    isOnEditMode: {
      type: Boolean,
      default: false
    },
    recipientsConfig: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      isLoading: false,
      isProcessing: false,
      sendToStartIsRunning: false,
      actionsAvailables: {},
      lumbreVersion: Number(process.env?.VUE_APP_DOCUMENTS_VERSION) || 1,
      formbuilderId: '',
      listbuilderId: '',
      documentTypeId: '',
      documentTypeName: '',
      documentTypeComments: '',
      documentTypeHidecCommentsOnFinished: false,
      isComunicable: false,
      isPublish: false,
      isPublic: false,
      signatureType: null,
      isOnLicense: false,
      canBeEditedByMe: false,
      canBeSignedByMe: false,
      canBeRejectedByMe: false,
      canBeSendToSignByMe: false,
      canBeReviewedByMe: false,
      canReSendEmailToExternalSigner: false,
      canAddReviewers: true,
      canAddExternalParticipants: true,
      canAddExternalRecipients: true,
      canDelegateSignature: false,
      documentInPdf: {},
      documentInPdfKey: 0,
      pdfSize: 0,
      lumbreNextParticipant: {},
      lumbreStatusName: null,
      initialForm: '',
      form: {
        workflow: [],
        collaborators: [],
        documentName: '',
        participants: [],
        workflowSectionsParticipants: [],
        documentSteps: {
          position: 0,
          items: []
        },
        recipients: {
          users: [],
          areas: [],
          subareas: [],
          groups: [],
          emails: []
        },
        viewers: {
          users: [],
          areas: [],
          subareas: []
        },
        shareWith: []
      },
      isError: false,
      isStarted: false,
      otpToken: '',
      documentHasAnyContent: false,
      errorOcurredOnSign: false,
      processDocument: false,
      attachmentDocuments: [],
      documentsPdfIndex: 0,
      showAssociatedProcesses: false,
      hasAssociatedProcesses: false,
      associatedProcesses: null,
      inPersonSignatureValidations: [],
      internalSignatureValidations: [],
      internalSignatureValidationsCapture: null,
      allowInPersonParticipant: false,
      signatureFilePath: null,
      signatureMetaFilePath: null,
      signatureGeoLocation: false,
      workflowSections: []
    }
  },
  validations: {
    form: {
      documentName: {
        required
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler (val) {
        this.documentHasAnyContent = true
      }
    }
  },
  computed: {
    // ...mapGetters('processes', ['createdByEditableProcess', 'editableProcessPath']),
    ...mapGetters('processes', ['processPathToGoBack']),

    disableInPersonGeoLocationValidations () {
      return process.env?.VUE_APP_ENABLE_IN_PERSON_GEOLOCATION_VALIDATIONS !== 'yes'
    },

    nextParticipantInCharacterOf () {
      return this.lumbreNextParticipant?.inCharacterOf || null
    },

    nextParticipantName () {
      return this.lumbreNextParticipant ? this.lumbreNextParticipant.firstname + ' ' + this.lumbreNextParticipant.lastname : ''
    },

    fromProcess () {
      return Boolean(this.processPathToGoBack)
    },
    context () {
      return {
        lumbreVersion: this.lumbreVersion,
        isStarted: this.isStarted,
        isOnEditMode: this.isOnEditMode,
        isComunicable: this.isComunicable
      }
    },
    disableFormbuilder () {
      if (this.isOnEditMode) {
        if (this.lumbreVersion === 1) {
          return !this.canBeEditedByMe
        } else if (this.lumbreVersion === 2) {
          return this.actionsAvailables?.canBeSeenByMe && !this.actionsAvailables?.canBeEditedByMe
        }
      } else {
        return false
      }
    },
    showFormBuilder () {
      if (
        (!this.isOnEditMode && this.formbuilderId) ||
        (
          (this.lumbreVersion === 1 && this.canBeEditedByMe) ||
          (this.lumbreVersion === 2 && this.actionsAvailables?.canBeEditedByMe) ||
          (this.lumbreVersion === 2 && this.actionsAvailables?.canBeSeenByMe)
        )
      ) {
        return true
      }
      return false
    },
    showDocumentViewer () {
      return !this.showFormBuilder
    },
    showDocumentInPdf () {
      return !!this.documentInPdf?.path
    },
    documentsPdfList () {
      return [
        this.documentInPdf,
        ...this.attachmentDocuments
      ]
    },
    showSubmitButton () {
      if (Number(this.lumbreVersion) === 1 && ((this.isOnEditMode && this.canBeEditedByMe) || !this.isOnEditMode)) {
        return true
      }
      if (
        Number(this.lumbreVersion) === 2 &&
        (!this.isOnEditMode || this.actionsAvailables?.canBeEditedByMe)
      ) {
        return true
      }
      return false
    },
    goBackOrCancelButton () {
      return !!this.$route?.params?.pathToRedirectOnCancel
    },
    toolbarItems () {
      let items = []
      if (this.isOnEditMode) {
        if (process.env?.VUE_APP_DISABLE_TAGS !== 'yes') {
          items.push('tags')
        }
        items = items.concat([
          'documentMovements',
          'auditMovements',
          null
        ])
        if (!this.hideComments) {
          items.unshift('comments')
        }
        if (this.canBeEditedByMe) {
          items.push('documentPreview')
          items.unshift('contentTemplates')
          if (!this.isPublic) {
            items.push('documentViewers')
          }
          if (this.form.shareWith?.length) {
            items.push('documentShareWith')
          }
        } else {
          items.push('documentModel')
          if (this.isComunicable) {
            items.push('documentRecipients')
          }
        }
      } else {
        items = items.concat([
          'contentTemplates',
          'documentPreview'
        ])
      }
      return items
    },
    toolbarConfig () {
      return {
        comments: {
          linkType: 'documents',
          disabled: this.disableComments
        },
        documentPreview: {
          documentTypeId: this.documentTypeId,
          dataToRender: this.form
        },
        documentModel: {
          formbuilderPath: this.formbuilderPath,
          dataPathOnEditMode: this.dataPathOnEditMode
        },
        documentRecipients: {
          recipients: this.form.recipients
        },
        documentViewers: {
          viewers: this.form.viewers
        },
        documentShareWith: {
          shareWith: this.form.shareWith
        },
        contentTemplates: {
          path: `/contenttemplate/${this.documentTypeId}/documenttypes/all`,
          template: this.form,
          reference: this.documentTypeId
        },
        auditMovements: {
          serviceName: 'documents'
        },
        tags: {
          serviceName: 'documents'
        }
      }
    },
    contentTemplateOptionsFiltered () {
      return this.contentTemplateOptions.filter(opt => {
        if (this.contentTemplateFilter === '') {
          return true
        } else {
          return opt.name.toLowerCase().search(this.contentTemplateFilter.toLowerCase()) !== -1
        }
      })
    },
    documentRecipients () {
      let recipients = []
      for (const key in this.form.recipients) {
        recipients = recipients.concat(this.form.recipients[key].map(r => {
          return {
            ...r,
            icon: this.setIcon(key)
          }
        }))
      }
      return recipients.length ? recipients : null
    },
    documentViewers () {
      let viewers = []
      for (const key in this.form.viewers) {
        viewers = viewers.concat(this.form.viewers[key].map(r => {
          return {
            ...r,
            icon: this.setIcon(key)
          }
        }))
      }
      return viewers.length ? viewers : null
    },
    // Calculo el ancho maximo que tiene el toolbar
    toolbarHeight () {
      return `max-height: calc(${this.pdfViewerHeight} + 39px + 3rem ${this.form?.documentSteps?.items?.length ? '+ 69px' : ''})`
    },
    formbuilderPath () {
      return `/formbuilder/${this.formbuilderId}`
    },
    dataPathOnEditMode () {
      if (this.isOnEditMode) {
        return `/documents/${this.$route?.params?.id}`
      } else {
        return ''
      }
    },
    stepsDirection: {
      get () {
        return this.form.participants.length > 6 ? 'vertical' : 'horizontal'
      },
      set () {}
    },
    stepsColumns: {
      get () {
        return this.stepsDirection === 'vertical' ? 'col-sm-12 col-md-2' : 'col-12'
      },
      set () {}
    },
    pdfViewerColumns: {
      get () {
        return this.stepsDirection === 'vertical' ? 'col-sm-12 col-md-10' : 'col-12'
      },
      set () {}
    },
    pdfViewerHeight: {
      get () {
        return this.stepsDirection === 'vertical' ? '100%' : '700px'
      },
      set () {}
    },
    disableComments () {
      if (this.documentTypeComments === 'always') {
        return false
      }
      if (this.documentTypeComments === 'in_progress') {
        if (this.lumbreStatusName !== 'Finalizado') {
          return false
        }
        return true
      }
      return false
    },
    hideComments () {
      if (this.documentTypeComments === 'in_progress' && this.lumbreStatusName === 'Finalizado' && this.documentTypeHidecCommentsOnFinished) {
        return true
      }
      if (this.documentTypeComments === 'never') {
        return true
      }
      return false
    }
  },
  methods: {
    ...mapMutations('processes', ['setDocument']),

    onHideCollaborators () {
      if (this.lumbreVersion === 2) {
        this.$refs.documentWorkflow.hideCollaborators = true
      }
    },
    onEditorChange (event) {
      if (this.lumbreVersion === 1) return
      const index = this.form.collaborators.findIndex(el => el.element === event.element.formObjectToSendToServerProperty)
      if (index > -1) {
        this.form.collaborators.splice(index, 1)
      }
      const collaborator = {
        ...event.editor,
        action: 'E',
        element: event.element.formObjectToSendToServerProperty
      }
      this.form.collaborators.push(collaborator)
      this.$refs.documentWorkflow.appendCollaborators(this.form.collaborators)
    },
    validation (position) {
      return _.get(_.cloneDeep(this.$v), position)
    },
    onContentTemplateChange (event) {
      for (const key in this.form) {
        if (event.hasOwnProperty(key)) {
          this.form[key] = event[key]
        }
      }
    },
    validateParticipants () {
      if (!this.documentHasWorkflowSections && this.form?.participants?.at(-1)?.action === 'R') {
        this.lmToastDangerMessage('Lo sentimos pero el último participante debe ser un firmante.', 'Error')
        throw new Error('El último participante debe ser un firmante.')
      }
    },
    async participantsLicencesNotification () {
      const participants = this.form.participants
        .filter(el => !el?.userType || !['external', 'inperson'].includes(el?.userType))
        .map(el => el.id)
      const res = await commonsApi.axiosPostWrapper('/documents/validate-participants', {
        participants
      })
      if (res.data.length) {
        let content = ''
        res.data.forEach(el => {
          let substitute = ''
          if (el.substitute) {
            substitute = `Esta siendo reemplazado por ${el.substitute.name}`
          }
          const user = this.form.participants.find(participant => participant.id === el.id)
          content += `<p>${user.name} tiene licencia hasta el ${el.end}. ${substitute}</p>`
        })
        this.$refs.lmConfirmationModalLicenses.setMessageProps(content)
        await this.$refs.lmConfirmationModalLicenses.lmShow()
        console.log('confirmo')
      }
    },
    async onSubmit () {
      try {
        this.isLoading = true

        // Validaciones
        this.validateParticipants()
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()

        const dataToSendToServer = {
          ...this.form,
          documentTypeId: this.documentTypeId
        }
        let res
        if (!this.isOnEditMode) {
          // nuevo
          res = await commonsApi.axiosPostWrapper('/documents/', dataToSendToServer)
          if (this.fromProcess) {
            this.lmNotificationSuccessMessage({
              message: this.$t('good_title'),
              description: this.$t('document_created_success_msg', { documentName: res.data?.documentName })
            })
          } else {
            this.lmModalSuccessWithButtons({
              modalClosable: false,
              modalKeyboard: false,
              resultSubtitle: '',
              message: this.$t('document_created_success_msg', { documentName: res.data?.documentName }),
              buttons: [
                {
                  btnLink: '/inbox',
                  btnText: this.$t('continue')
                },
                {
                  btnLink: `/documentos/${this.documentTypeId}/editar/${res.data._id}`,
                  btnText: this.$t('view_document')
                }
              ]
            })
          }
        } else {
          // editar
          res = await commonsApi.axiosPutWrapper(`/documents/update/${this.$route?.params?.id}`, dataToSendToServer)
          this.lmNotificationSuccessMessage({
            message: this.$t('good_title'),
            description: this.$t('document_updated_success_msg', { documentName: res.data?.documentName })
          })
        }
        // Si este documento se crea como parte de un proceso (trámite)
        if (this.fromProcess) {
          this.setDocument({
            id: res.data._id,
            name: res.data.documentName
          })
          this.$router.push(this.processPathToGoBack)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onResendTokenToSigner () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper('/documents/resendEmail-to-external', {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage('Se reenvió la notificación correctamente', 'Enviado con éxito')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    async onSendToStart () {
      this.isLoading = true
      try {
        this.validateParticipants()
        await this.validateForm(this.$v)
        await this.$refs.lmFormbuilder.validate()
        await this.participantsLicencesNotification()

        const targetEndpoint = this.isOnEditMode ? '/documents/start' : '/documents/createandstart'
        const res = await commonsApi.axiosPostWrapper(targetEndpoint, {
          id: this.$route.params.id,
          documentTypeId: this.documentTypeId,
          ...this.form
        })

        if (this.fromProcess) {
          this.setDocument({
            id: res.data._id,
            name: res.data.documentName
          })
          this.lmNotificationSuccessMessage({
            message: this.$t('good_title'),
            description: this.$t('start_document_confirmation', { documentName: this.form?.documentName })
          })
          // this.$router.push(this.processPathToGoBack)
        }
        if (res.data.action === 'inmediateSign' || res.data.action === 'inmediateReview') {
          this.lmModalInformationMessage({
            resultSubtitle: '',
            buttons: [
              {
                btnText: this.$t('i_will_do_it_later'),
                btnType: 'danger',
                btnGhost: true,
                btnLink: this.fromProcess ? this.processPathToGoBack : '/inbox'
              },
              {
                btnText: this.$t('continue'),
                btnCallback: async () => {
                  if (process?.env?.VUE_APP_LUMBRE_LICENSES_ENABLED === 'yes') {
                    if (res.data.action === 'inmediateSign' && !this.isOnLicense) {
                      await this.loadContentOnEditMode(res.data.docId)
                      await this.onBeforeSign(res.data.docId, false)
                    }
                    if (res.data.action === 'inmediateReview' && !this.isOnLicense) {
                      await this.onReview(res.data.docId)
                    }
                  } else {
                    if (res.data.action === 'inmediateSign') {
                      await this.loadContentOnEditMode(res.data.docId)
                      await this.onBeforeSign(res.data.docId, false)
                    }
                    if (res.data.action === 'inmediateReview') {
                      await this.onReview(res.data.docId)
                    }
                  }
                }
              }
            ],
            message: res.data.action === 'inmediateReview' ? this.$t('confirmations.immediate_review_document_v1') : this.$t('confirmations.immediate_sign_document_v1')
          })
        } else {
          this.$router.push(this.fromProcess ? this.processPathToGoBack : '/inbox')
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },

    // onAfterProcessStart (res, redirectAfterFinished, inmediateActionCompleted = false) {
    //   // Si este documento se crea como parte de un proceso (trámite)
    //   if (this.fromProcess) {
    //     this.setDocument({
    //       id: res.data._id,
    //       name: res.data.documentName
    //     })
    //     this.lmNotificationSuccessMessage({
    //       message: this.$t('good_title'),
    //       description: this.$t('start_document_confirmation', { documentName: this.form?.documentName })
    //     })
    //     this.$router.push(this.processPathToGoBack)
    //   } else if (redirectAfterFinished) {
    //     if (inmediateActionCompleted !== 'inmediateReview' && inmediateActionCompleted !== 'inmediateSign') {
    //       setTimeout(() => {
    //         this.lmModalSuccessWithButtons({
    //           modalClosable: false,
    //           modalKeyboard: false,
    //           resultSubtitle: '',
    //           message: this.$t('document_started_success_msg', { documentName: this.form?.documentName }),
    //           buttons: [
    //             {
    //               btnLink: '/inbox',
    //               btnText: this.$t('continue')
    //             },
    //             {
    //               btnText: this.$t('view_document'),
    //               btnCallback: async () => {
    //                 if (this.isOnEditMode) {
    //                   await this.loadContentOnEditMode()
    //                 } else {
    //                   this.$router.push(`/documentos/${this.documentTypeId}/editar/${res.data._id}`)
    //                 }
    //               }
    //             }
    //           ]
    //         })
    //       }, 650)
    //     } else {
    //       setTimeout(() => {
    //         this.lmModalSuccessWithButtons({
    //           modalClosable: false,
    //           modalKeyboard: false,
    //           resultSubtitle: '',
    //           message: inmediateActionCompleted === 'inmediateReview' ? this.$t('document_approved_success_msg', { documentName: this.form?.documentName }) : this.$t('document_signed_success_msg', { documentName: this.form?.documentName }),
    //           buttons: [
    //             {
    //               btnLink: '/inbox',
    //               btnText: this.$t('continue')
    //             },
    //             {
    //               btnText: this.$t('view_document'),
    //               btnCallback: async () => {
    //                 if (this.isOnEditMode) {
    //                   await this.loadContentOnEditMode()
    //                 } else {
    //                   this.$router.push(`/documentos/${this.documentTypeId}/editar/${res.data._id}`)
    //                 }
    //               }
    //             }
    //           ]
    //         })
    //       }, 650)
    //     }
    //   }
    // },

    // onSignConfirmation () {
    //   this.isProcessing = true
    //   this.lmModalInformationMessage({
    //     modalClosable: false,
    //     modalKeyboard: false,
    //     resultSubtitle: '',
    //     buttons: [
    //       {
    //         btnText: this.$t('cancel'),
    //         btnType: 'danger',
    //         btnGhost: true,
    //         btnCallback: () => {
    //           this.isProcessing = false
    //         }
    //       },
    //       {
    //         btnText: this.$t('sign'),
    //         btnCallback: () => {
    //           const documentId = this.$route.params?.id
    //           this.onSign(documentId)
    //         }
    //       }
    //     ],
    //     message: this.$t('confirmations.start_sign_confirmation')
    //   })
    // },

    async onBeforeSign (documentId = this.$route.params.id, showConfirmation = true) {
      this.isProcessing = true
      if (!this.disableInPersonGeoLocationValidations) {
        this.signatureGeoLocation = await this.getLocation()
      }

      try {
        const nextSignerUserType = this.form.lumbreNextSigner?.userType || null

        // TODO. si es un usuario presencial o el usuario actual no tiene firma en el store
        // muestro el lmSignaturePad

        const { signature, id: loggedUserId } = this.$store.getters['session/userInfo']

        let base64File = null
        let signatureFilePath = null
        let signatureMeta = null
        let signaturePersistence = true
        let signerId = this.form.lumbreNextSigner.id

        // Firmante presencial
        if (nextSignerUserType === 'inperson') {
          // Geolocalización
          if (!this.disableInPersonGeoLocationValidations) {
            if (this.inPersonSignatureValidations.includes('geolocation')) {
              if (!this.signatureGeoLocation) {
                this.lmModalInformationMessage({
                  resultSubtitle: '',
                  message: 'La geolocalización es requerida para firmar este documento.'
                })
                throw new Error('La geolocalizacion es obligatoria.')
              }
            }
          }

          // lmSignaturePad
          if (this.inPersonSignatureValidations.includes('holographic')) {
            base64File = await this.$refs.lmSignaturePad.open()
          }

          // WacomSignaturePad
          if (this.inPersonSignatureValidations.includes('holographic_wacom')) {
            const data = await this.$refs.WacomSignaturePad.open(this.nextParticipantName, this.nextParticipantInCharacterOf)
            base64File = data.img
            signatureMeta = data
          }
        }

        // Firmante interno
        if (nextSignerUserType === 'internal') {
          // Si el proximo firmante es interno, siempre deberia ser el usuario logueado
          // puede ser un reemplazante
          signerId = loggedUserId

          // Geolocalización
          if (!this.disableInPersonGeoLocationValidations) {
            if (this.internalSignatureValidations.includes('geolocation')) {
              if (!this.signatureGeoLocation) {
                this.lmModalInformationMessage({
                  resultSubtitle: '',
                  message: 'La geolocalización es requerida para firmar este documento.'
                })
                throw new Error('La geolocalizacion es obligatoria.')
              }
            }
          }

          // Firma del perfil del usuario
          if (this.internalSignatureValidations.includes('holographic_simple')) {
            if (!signature) {
              base64File = await this.$refs.lmSignaturePad.open()
            } else {
              signatureFilePath = signature
            }
          }
          // Firmar en este momento
          if (this.internalSignatureValidations.includes('holographic_advanced')) {
            if (this.internalSignatureValidationsCapture === 'holographic_simple_capture') {
              // lmSignaturePad
              base64File = await this.$refs.lmSignaturePad.open()
            } else if (this.internalSignatureValidationsCapture === 'holographic_wacom_capture') {
              // WacomSignaturePad
              const data = await this.$refs.WacomSignaturePad.open(this.nextParticipantName, this.nextParticipantInCharacterOf)
              base64File = data.img
              signatureMeta = data
            }
            signaturePersistence = false
          }
        }

        if (base64File) {
          const { data: filePath } = await commonsApi.axiosPutWrapper('/users/upload-signature/' + signerId, {
            signature: base64File,
            persistence: signaturePersistence
          })

          signatureFilePath = filePath

          // Si puedo firmar, la firma es persistente y es un firmante interno,
          // guardo su firma en vuex
          if (
            this.canBeSignedByMe &&
            signaturePersistence &&
            nextSignerUserType === 'internal'
          ) {
            this.$store.commit('session/mutateSignature', filePath)
          }
        }
        if (signatureMeta) {
          await commonsApi.axiosPutWrapper('/documents/upload-signature-meta/' + documentId, {
            signature: signatureMeta,
            signer: this.form.lumbreNextSigner
          })
          this.signatureMetaFilePath = signatureMeta
        }
        if (signatureFilePath) {
          this.signatureFilePath = signatureFilePath
        }

        if (nextSignerUserType === 'inperson') {
          await this.onSign(documentId)
          this.isProcessing = false
        } else {
          if (showConfirmation) {
            this.lmModalInformationMessage({
              modalClosable: false,
              modalKeyboard: false,
              resultSubtitle: '',
              buttons: [
                {
                  btnText: this.$t('cancel'),
                  btnType: 'danger',
                  btnGhost: true,
                  btnCallback: () => {
                    this.isProcessing = false
                  }
                },
                {
                  btnText: this.$t('sign'),
                  btnCallback: () => {
                    this.onSign(documentId)
                  }
                }
              ],
              message: this.$t('confirmations.start_sign_confirmation')
            })
          } else {
            this.onSign(documentId)
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isProcessing = false
      }
    },

    async onSign (id) {
      try {
        this.isProcessing = true
        const documentId = this.$route.params?.id || id
        this.errorOcurredOnSign = false
        const inPersonSign = this.form.lumbreNextSigner?.userType === 'inperson' && this.canDelegateSignature

        if (inPersonSign) {
          await commonsApi.axiosPostWrapper(`/documents/sign`, {
            id: documentId,
            signature: this.signatureFilePath,
            location: this.signatureGeoLocation
          })

          this.lmModalSuccessWithButtons({
            modalClosable: false,
            modalKeyboard: false,
            resultSubtitle: '',
            message: this.$t('document_inperson_signed_success_msg', { documentName: this.form?.documentName }),
            buttons: [
              {
                btnLink: '/inbox',
                btnText: this.$t('continue')
              },
              {
                btnText: this.$t('view_document'),
                btnCallback: async () => {
                  await this.loadContentOnEditMode()
                }
              }
            ]
          })
        } else {
          if (this?.signatureType === 'electronic') {
            this.isLoading = true
            await commonsApi.axiosPostWrapper('/documents/generateotp', {
              id: documentId
            })
            this.isLoading = false
            this.$refs.lmValidationModalFirmarToken.setIsCommentProps(true, true, 'Codigo', 'token')
            await this.$refs.lmValidationModalFirmarToken.lmShow()
            await commonsApi.axiosPostWrapper(`/documents/sign`, {
              id: documentId,
              token: this.otpToken,
              signature: this.signatureFilePath,
              location: this.signatureGeoLocation
            })
            this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
          } else if (this?.signatureType === 'electronic_without_otp') {
            this.isLoading = true
            await commonsApi.axiosPostWrapper(`/documents/sign`, {
              id: documentId,
              token: this.otpToken,
              signature: this.signatureFilePath
            })
            this.isLoading = false
            this.lmToastSuccessMessage(`Se firmó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento firmado.')
          } else if (this?.signatureType === 'digital') {
            const nextSignerUserType = this.form.lumbreNextSigner?.userType || null
            if (
              nextSignerUserType === 'internal' &&
              this.internalSignatureValidations.includes('holographic_advanced')
            ) {
              await commonsApi.axiosPostWrapper('/documenthistory/append-movement', {
                id: documentId,
                participant: this.form.lumbreNextSigner,
                data: {
                  signatureFilePath: this.signatureFilePath,
                  signatureMeta: this.signatureMetaFilePath,
                  location: this.signatureGeoLocation
                }
              })
            }

            if (this.enableLumbredocsSigner) {
              this.isLoading = true
              try {
                await this.checkLumbreDocsSigner()
              } catch (error) {
                this.errorOcurredOnSign = true
              }
              const r = await commonsApi.axiosGetWrapper(`/documents/generatejnlpV2/${documentId}`)
              await commonsApi.axiosPostWrapper('http://localhost:8081/initSR', {
                url: r.data.targetURL
              })
            } else {
              const tempLink = document.createElement('a')
              tempLink.href = `${process.env.VUE_APP_API_URL}documents/generatejnlp/${documentId}`
              tempLink.setAttribute('download', `documento_afirmar.jnlp`)
              tempLink.click()
              this.lmToastSuccessMessage(`Para continuar debe finalizar el proceso de firma desde la aplicacion.`, 'Enviado al Firmador.')
            }
          }
          if (!this.errorOcurredOnSign) {
            if (this.fromProcess) {
              this.$router.push(this.processPathToGoBack)
            } else if (process.env.VUE_APP_SOCKET_ENABLED !== 'yes') {
              this.$router.push('/inbox')
            } else if (!this.isOnEditMode) {
              this.$router.push(`/documentos/${this.documentTypeId}/editar/${documentId}`)
            }
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
        this.isProcessing = false

        this.signatureFilePath = null
        this.signatureMetaFilePath = null
        this.signatureGeoLocation = false
      }
    },

    getLocation () {
      return new Promise((resolve, reject) => {
        const options = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }

        navigator.geolocation.getCurrentPosition(
          (position) => resolve({
            timestamp: position.timestamp,
            accuracy: position.coords.accuracy,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }),
          // (error) => reject(error),
          (error) => {
            console.log(error)
            resolve(false)
          },
          options
        )
      })
    },

    onReviewConfirmation () {
      this.isProcessing = true
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultSubtitle: '',
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
              this.isProcessing = false
            }
          },
          {
            btnText: this.$t('continue'),
            btnCallback: () => {
              const documentId = this.$route.params?.id
              this.onReview(documentId)
            }
          }
        ],
        message: this.$t('confirmations.review_confirmation')
      })
    },

    async onReview (id) {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/review`, {
          id: this.$route.params.id || id
        })
        this.lmToastSuccessMessage(`Se revisó el documento "${this.form?.documentName || ' el documento'}" correctamente.`, 'Documento revisado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onTake () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/take`, {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage(`Se tomó el documento "${this.form?.documentName || ' el documento'}" correctamente.`, 'Documento tomado.')
        setTimeout(() => {
          this.$router.go()
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onReject () {
      try {
        this.isLoading = true
        await commonsApi.axiosPostWrapper(`/documents/reject`, {
          id: this.$route.params.id
        })
        this.lmToastSuccessMessage(`Se rechazó el documento "${this.form?.documentName || ' el documento'}" correctamente.`, 'Documento rechazado.')
        setTimeout(() => {
          this.$router.push('/inbox')
        }, 650)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDelete () {
      try {
        this.isLoading = true
        await commonsApi.axiosPutWrapper(`/documents/delete/${this.$route.params.id}`)
        this.lmToastSuccessMessage(`Se eliminó ${this.form?.documentName || ' el documento'} correctamente.`, 'Documento eliminado.')
        if (this.fromProcess) {
          this.$router.push(this.processPathToGoBack)
        } else {
          setTimeout(() => {
            this.$router.push('/inbox')
          }, 650)
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onCancel () {
      if (this.goBackOrCancelButton) {
        this.$router.push(this.$route.params.pathToRedirectOnCancel)
      } else if (this.fromProcess) {
        this.$router.push(this.processPathToGoBack)
      } else {
        this.$router.push('/inbox')
      }
    },
    updateForm (event) {
      this.form = Object.assign({}, this.form, event)
    },
    validateForm (validations) {
      return new Promise((resolve, reject) => {
        let error = false
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this.form) {
            if (validations.form.hasOwnProperty(key)) {
              if (validations.form[key].$error) {
                const selector = this.$el.querySelector(`#${key}`)
                selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },
    async loadAttachmentContent (data) {
      const arr = []
      if (this.formbuilderId) {
        const formbuilderResponse = await commonsApi.axiosGetWrapper(`/formbuilder/${this.formbuilderId}`)
        const uploadersFormElement = formbuilderResponse.data.formElements.filter(el => el.componentName === 'LmUploadFiles')
        if (uploadersFormElement.length) {
          for (const element of uploadersFormElement) {
            if (data[element.formObjectToSendToServerProperty]) {
              arr.push(data[element.formObjectToSendToServerProperty])
            }
          }
        }
      }
      if (arr.length) {
        this.attachmentDocuments = arr.flat().map(el => {
          return {
            ...el,
            path: process.env.VUE_APP_API_URL_STATIC_ASSETS + el.path
          }
        })
      }
    },
    async loadContentOnEditMode (id = null) {
      if (this.isOnEditMode || id) {
        try {
          const documentId = id || this.$route.params.id
          this.isError = false
          this.isLoading = true
          const document = await commonsApi.axiosGetWrapper(`/documents/${documentId}`)
          this.form.documentName = document.data.documentName
          this.form.participants = document.data?.participants || []
          this.form.workflowSectionsParticipants = document.data?.workflowSectionsParticipants || []
          this.form.recipients = document.data?.recipients || {}
          this.form.collaborators = document.data?.collaborators || []
          this.form.lumbreNextSigner = document.data?.lumbreNextSigner || {}
          this.form.workflow = document.data?.workflow || []
          if (document.data.documentSteps) {
            this.form.documentSteps = document.data.documentSteps
          }
          this.form.shareWith = document.data?.shareWith || []
          this.form.viewers = document.data?.viewers || {}
          this.canDelegateSignature = document.data?.canDelegateSignature || false
          this.canBeEditedByMe = document.data?.canBeEditedByMe || false
          this.canBeSignedByMe = (document.data?.canBeSignedByMe && !(document.data?.documentInPdf?.error === true)) || false
          this.canBeRejectedByMe = document.data?.canBeRejectedByMe || false
          this.canBeSendToSignByMe = document.data?.canBeSendToSignByMe || false
          this.documentInPdf = document.data?.documentInPdf || {}
          this.documentInPdfKey++
          this.pdfSize = document.data?.pdfSize || 0
          this.lumbreNextParticipant = document.data?.lumbreNextParticipant || {}
          this.canReSendEmailToExternalSigner = document.data?.canReSendEmailToExternalSigner || false
          this.canBeReviewedByMe = (document.data?.canBeReviewedByMe && !(document.data?.documentInPdf?.error === true)) || false
          this.lumbreStatusName = document.data?.lumbreStatusName || null
          this.lumbreVersion = document.data?.lumbreVersion || process.env?.VUE_APP_DOCUMENTS_VERSION || 1
          this.isStarted = document.data?.started || false
          this.createdBy = document.data?.createdBy.user || null
          this.actionsAvailables = document.data?.actions_availables || {}

          if (Object.keys(this.documentInPdf).length) {
            this.documentInPdf = { ...this.documentInPdf, path: this.documentInPdf?.src }
          }

          await this.loadAttachmentContent(document.data)
          await this.getAssociations()
        } catch (error) {
          console.log(error)
          this.isError = true
        } finally {
          this.isLoading = false
        }
      }
    },
    setToken (data) {
      this.otpToken = data.comment
    },
    async getAssociations () {
      const response = await commonsApi.axiosGetWrapper(`/documents/${this.$route.params.id}/associated-processes`)
      if (response.data.length) {
        this.hasAssociatedProcesses = true
        this.associatedProcesses = response.data
      }
    },
    // Refresco los components de vue en caso de permanecer en la misma view
    // Nota. esto no deberia ser necesario pero lo es ya que algunos componentes
    // no tienen la capacidad de actualizarse mediante una escucha activa de los datos
    refreshVueComponents () {
      this.isLoading = true
      this.$nextTick(() => { this.isLoading = false })
    },
    async onRefresh () {
      await this.loadContentOnEditMode()
      this.refreshVueComponents()
    }
  },
  async created () {
    try {
      this.isLoading = true
      this.initialForm = JSON.stringify(this.form)

      if (process?.env?.VUE_APP_LUMBRE_LICENSES_ENABLED === 'yes') {
        const isOnLicense = await commonsApi.axiosGetWrapper('/licenses/imonlicense')
        this.isOnLicense = isOnLicense.data
      } else {
        this.isOnLicense = false
      }

      const documentType = await commonsApi.axiosGetWrapper(`/documenttypes/${this.$route.params.docTypeId}`)
      this.formbuilderId = documentType.data.formbuilderId
      this.listbuilderId = documentType.data.listbuilderId
      this.documentTypeId = documentType.data._id
      this.documentTypeName = documentType.data.typeName
      this.isComunicable = documentType.data.typeComunicable === 'comunicable'
      this.isPublish = documentType.data.publish
      this.isPublic = documentType.data.typeVisibility === 'public'
      this.signatureType = documentType.data.typeSignature
      this.documentTypeComments = documentType.data?.typeComments || 'always'
      this.documentTypeHidecCommentsOnFinished = documentType.data?.typeHideCommentsOnFinished || false
      this.canAddReviewers = documentType.data?.typeAllowReviewers
      this.canAddExternalParticipants = documentType.data?.typeAllowExternalParticipants
      this.canAddExternalRecipients = documentType.data?.typeAllowExternalRecipients
      this.allowInPersonParticipant = documentType.data?.allowInPersonParticipant || false
      this.inPersonSignatureValidations = documentType.data?.inPersonSignatureValidations || []
      this.internalSignatureValidations = documentType.data?.internalSignatureValidations || []
      this.internalSignatureValidationsCapture = documentType.data?.internalSignatureValidationsCapture || null
      this.workflowSections = documentType.data?.workflowSections || []
      this.form.workflowSectionsParticipants = this.workflowSections

      await this.loadContentOnEditMode()
      if (!this.isOnEditMode) {
        const user = this.$store.getters['session/userInfo']
        this.form.workflow.push([{
          id: user.id,
          name: `${user.lastname}, ${user.firstname}`,
          action: 'C'
        }])
      }
      if (
        this.showDocumentInPdf &&
        !this.form.workflow.flat().some(el => el.action === 'E') &&
        this.lumbreVersion === 2
      ) {
        this.$refs.documentWorkflow.hideCollaborators = true
      }

      // Firma desde el enlace de una notificacion push
      if (
        this.isOnEditMode &&
        this.$route.query.immediateSign === 'true' &&
        this.canBeSignedByMe
      ) {
        await this.onBeforeSign()
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      if (from.name === 'process-nuevo' || from.name === 'process-editar') {
        vm.processDocument = from.fullPath
      }
    })
  }
}
</script>

<style lang="sass" scoped>
  #pdfViewer
    min-height: 500px
  .next-btn, .prev-btn
    color: white
    font-size: 1.2rem
    bottom: 50%
    transform: translateY(50%)
  .prev-btn
    left: 10px
  .next-btn
    right: 10px
  .page-content
    width: calc(100% + 1rem)
    min-height: calc(100vh - 70px - 60px - 95px)
    margin-left: auto
    display: grid
    grid-template-columns: calc(100% - 100px) 100px
    @media all and (max-width: $sm)
      grid-template-columns: calc(100% - 10px) 10px
  .dotted-hr
    border: none
    border-top: 1px dashed #999
    margin: 10px 0
</style>
